import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Calendar,
  Clock,
  Save,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Map
} from 'lucide-react';
import styles from './AttendanceDrawer.module.css';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../../components/Common/Select/PaginatedSelect';

const AttendanceDrawer = ({ isOpen, onClose, onSave, record = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    project_site_id: '',
    date: '',
    punch_in_time: '',
    punch_out_time: '',
    status: '',
    is_manual_override: false,
    override_by: '',
    reason: ''
  });

  const [loading, setLoading] = useState(false);
  const statusOptions = [
    { value: 'FULL_DAY', label: 'Full Day' },
    { value: 'HALF_DAY', label: 'Half Day' },
    { value: 'FIRST_HALF', label: 'First Half' },
    { value: 'SECOND_HALF', label: 'Second Half' },
    { value: 'ABSENT', label: 'Absent' },
    { value: 'HOLIDAY', label: 'Holiday' },
    { value: 'LEAVE', label: 'Leave' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'PENDING_ADMIN_APPROVAL', label: 'Pending Approval' }
  ];

  useEffect(() => {
    const fetchSessionData = async () => {
      if (record && isOpen) {
        setLoading(true);
        try {
          const response = await api.get(`/visit-sessions/${record.id}`);
          const resData = response.data?.data || response.data;
          const sessionData = resData.visit_session || resData;

          let fetchedStatus = sessionData.status || resData.attendance_summary?.status || 'FULL_DAY';
          if (fetchedStatus.toUpperCase() === 'PRESENT') fetchedStatus = 'FULL_DAY';

          setFormData({
            employee_id: sessionData.employee_id || '',
            project_site_id: sessionData.project_id || sessionData.project_site_id || '',
            date: sessionData.visit_date || sessionData.date || '',
            punch_in_time: sessionData.punch_in ? new Date(sessionData.punch_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
            punch_out_time: sessionData.punch_out ? new Date(sessionData.punch_out).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
            status: fetchedStatus,
            is_manual_override: !!sessionData.is_manual_override,
            override_by: sessionData.override_by || '',
            reason: sessionData.reason || ''
          });
        } catch (error) {
          console.error('Error fetching session details:', error);
          toast.error('Failed to load attendance record details');
          // Fallback to record prop

          let fallbackStatus = record.status || record?.attendance?.status || 'FULL_DAY';
          if (fallbackStatus.toUpperCase() === 'PRESENT') fallbackStatus = 'FULL_DAY';

          setFormData({
            employee_id: record.employee_id || '',
            project_site_id: record.project_id || record.project_site_id || '',
            date: record.date || record.visit_date || record.attendance_date || '',
            punch_in_time: record.punch_in ? new Date(record.punch_in).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
            punch_out_time: record.punch_out ? new Date(record.punch_out).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
            status: fallbackStatus,
            is_manual_override: !!record.is_manual_override,
            override_by: record.override_by || '',
            reason: ''
          });
        } finally {
          setLoading(false);
        }
      } else if (!record && isOpen) {
        setFormData({
          employee_id: '',
          project_site_id: '',
          date: '',
          punch_in_time: '',
          punch_out_time: '',
          status: 'FULL_DAY',
          is_manual_override: true,
          override_by: JSON.parse(localStorage.getItem('user') || '{}').name || '',
          reason: ''
        });
      }
    };

    fetchSessionData();
  }, [isOpen, record]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      date: date ? date.toLocaleDateString('en-CA') : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const checkInDateTime = formData.punch_in_time ? `${formData.date} ${formData.punch_in_time}` : null;
      const checkOutDateTime = formData.punch_out_time ? `${formData.date} ${formData.punch_out_time}` : null;

      if (record) {
        // 1. Update punch timings
        await api.post('/attendance/edit-punch', {
          visit_session_id: record.id,
          punch_in: checkInDateTime,
          punch_out: checkOutDateTime,
          reason: formData.reason
        });

        // 2. Explicitly override the status
        await api.post('/attendance/override', {
          employee_id: formData.employee_id,
          attendance_date: formData.date,
          status: formData.status,
          reason: formData.reason,
          admin_id: JSON.parse(localStorage.getItem('user') || '{}').id
        });

        toast.success('Punch timings and status updated successfully');
      } else {
        const payload = {
          employee_id: formData.employee_id,
          project_id: formData.project_site_id,
          punch_in_time: checkInDateTime,
          status: formData.status,
        };
        await api.post('/visit-sessions/manual-checkin', payload);
        toast.success('Attendance added successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(error.response?.data?.message || 'Failed to save attendance');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{record ? 'Edit Attendance' : 'Manual Attendance'}</h2>
            <p>Enter punch times and site details for employee.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {/* Employee Selection */}
            <div className={styles['form-group']}>
              <label><User size={16} /> Employee <span className={styles['required-star']}>*</span></label>
              <PaginatedSelect
                endpoint="/employees-dropdown"
                valueKey="id"
                labelKey="employee_name"
                value={formData.employee_id}
                onChange={(val) => setFormData(prev => ({ ...prev, employee_id: val || '' }))}
                placeholder="Select Employee"
                required
              />
            </div>

            {/* Site Selection */}
            <div className={styles['form-group']}>
              <label><Map size={16} /> Project Site <span className={styles['required-star']}>*</span></label>
              <PaginatedSelect
                endpoint="/projects-dropdown"
                valueKey="id"
                labelKey="project_name"
                value={formData.project_site_id}
                onChange={(val) => setFormData(prev => ({ ...prev, project_site_id: val || '' }))}
                placeholder="Select Site"
                required
              />
            </div>

            {/* Date */}
            <div className={styles['form-group']}>
              <label><Calendar size={16} /> Date <span className={styles['required-star']}>*</span></label>
              <CustomDatePicker
                selected={formData.date}
                onChange={handleDateChange}
                required
              />
            </div>

            {/* Punch In & Punch Out Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className={styles['form-group']}>
                <label><Clock size={16} /> Punch In</label>
                <input
                  type="time"
                  name="punch_in_time"
                  value={formData.punch_in_time}
                  onChange={handleChange}
                  className={styles['form-input']}
                />
              </div>

              {record && (
                <div className={styles['form-group']}>
                  <label><Clock size={16} /> Punch Out</label>
                  <input
                    type="time"
                    name="punch_out_time"
                    value={formData.punch_out_time}
                    onChange={handleChange}
                    className={styles['form-input']}
                  />
                </div>
              )}
            </div>

            {/* Reason Field for Editing */}
            {record && (
              <div className={styles['form-group']}>
                <label><AlertCircle size={16} /> Reason for Change <span className={styles['required-star']}>*</span></label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  className={styles['form-input']}
                  placeholder="Explain why the punch timings are being edited..."
                  required={!!record}
                  rows={3}
                  style={{ resize: 'none' }}
                />
              </div>
            )}

            {/* Status (Shown for both new check-in and edit overrides) */}
            <div className={styles['form-group']}>
              <label><ShieldCheck size={16} /> Status</label>
              <CustomSelect
                options={statusOptions}
                value={statusOptions.find(s => s.value === formData.status)}
                onChange={(opt) => handleSelectChange('status', opt)}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{record ? 'Update Record' : 'Save Attendance'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceDrawer;
